import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useGetMeetingAgendaQuery,
  useUpdateMeetingAgendaStatusMutation,
} from "../api/apiSlice";

export default function MeetingAgenda() {
  const meetingId = useSelector((state) => state.meeting.meetingId);
  const [expandedId, setExpandedId] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const organizerId = useSelector((state) => state.meeting.organizerId);
  const [newAgendaText, setNewAgendaText] = useState("");
  // Use the hook to fetch meeting agenda
  // Prevent calling the API if meetingId is not set yet

  const { data, error, isLoading, refetch } = useGetMeetingAgendaQuery(
    { meetingId, organizerId },
    {
      skip: !meetingId || !organizerId, // Skip the query if required params are not available
    }
  );

  const [updateStatus] = useUpdateMeetingAgendaStatusMutation();

  // Reset initialization state when meeting changes
  useEffect(() => {
    setHasInitialized(false);
    setExpandedId(null);
  }, [meetingId]);

  // Auto-expand the first Open agenda item by default on load
  useEffect(() => {
    if (data && data.length > 0 && !hasInitialized) {
      const firstOpen = data.find((item) => item.status === "Open");
      if (firstOpen) {
        setExpandedId(firstOpen.meetingAgendaId);
      } else {
        setExpandedId(data[0].meetingAgendaId);
      }
      setHasInitialized(true);
    }
  }, [data, hasInitialized]);

  const onToggle = async (agendaItem) => {
    const newStatus = agendaItem.status === "Open" ? "Done" : "Open";

    try {
      await updateStatus({
        meetingId,
        meetingAgendaId: agendaItem.meetingAgendaId,
        status: newStatus,
      }).unwrap();
      refetch();

      // If we marked it as Done, auto-expand the next open item
      if (newStatus === "Done") {
        const currentIndex = data.findIndex((x) => x.meetingAgendaId === agendaItem.meetingAgendaId);
        let nextItem = null;
        for (let i = currentIndex + 1; i < data.length; i++) {
          if (data[i].status === "Open") {
            nextItem = data[i];
            break;
          }
        }
        if (!nextItem) {
          nextItem = data.find((x, idx) => idx !== currentIndex && x.status === "Open");
        }
        if (!nextItem && currentIndex + 1 < data.length) {
          nextItem = data[currentIndex + 1];
        }
        if (nextItem) {
          setExpandedId(nextItem.meetingAgendaId);
        }
      }
    } catch (err) {
      console.error("Failed to update agenda status", err);
    }
  };

  const toggleExpand = (item) => {
    setExpandedId(expandedId === item.meetingAgendaId ? null : item.meetingAgendaId);
  };

  const totalItems = data?.length || 0;
  const doneItems = data?.filter((item) => item.status === "Done").length || 0;
  const progressPercent = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  return (
    <section className="widget-card">
      <h3 className="widget-title">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="8" y1="6" x2="21" y2="6" />
          <line x1="8" y1="12" x2="21" y2="12" />
          <line x1="8" y1="18" x2="21" y2="18" />
          <line x1="3" y1="6" x2="3.01" y2="6" />
          <line x1="3" y1="12" x2="3.01" y2="12" />
          <line x1="3" y1="18" x2="3.01" y2="18" />
        </svg>
        Meeting Agenda
      </h3>

      {isLoading && (
        <div style={{ padding: "16px 0", display: "flex", gap: "8px", flexDirection: "column" }}>
          <div style={{ height: "30px", background: "var(--skeleton-bg)", borderRadius: "6px", width: "80%" }} />
          <div style={{ height: "30px", background: "var(--skeleton-bg)", borderRadius: "6px", width: "90%" }} />
        </div>
      )}

      {error && (
        <div style={{ color: "var(--danger)", fontSize: "0.85rem", padding: "8px 0" }}>
          Failed to load meeting agenda. Please try again.
        </div>
      )}

      {!isLoading && !error && (
        <>
          {totalItems > 0 ? (
            <div className="agenda-progress-wrapper">
              <div className="progress-info">
                <span>Task Completion</span>
                <strong>{doneItems} of {totalItems} ({progressPercent}%)</strong>
              </div>
              <div className="progress-bar-bg">
                <div className="progress-bar-fill" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          ) : (
            <div className="no-cards-placeholder" style={{ padding: "20px", marginBottom: "16px" }}>
              No agenda items set for this meeting. Add tasks below.
            </div>
          )}

          <ul className="agenda-list">
            {data?.map((item) => {
              const isExpanded = expandedId === item.meetingAgendaId;
              return (
                <li
                  key={item.meetingAgendaId}
                  className={`agenda-item ${item.status === "Done" ? "done" : ""} ${
                    isExpanded ? "expanded" : ""
                  }`}
                  onClick={() => toggleExpand(item)}
                  style={{ cursor: "pointer" }}
                >
                  <div className="agenda-header-row">
                    <label className="agenda-label" onClick={(e) => e.stopPropagation()}>
                      <div className="checkbox-container">
                        <input
                          type="checkbox"
                          checked={item.status === "Done"}
                          onChange={() => onToggle(item)}
                        />
                        <span className="checkmark" />
                      </div>
                      <span className="agenda-text">{item.agendaText}</span>
                    </label>
                    <div className="agenda-actions">
                      {item.agendaDescription && (
                        <span className="agenda-toggle-icon">
                          <svg
                            width="10"
                            height="10"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="3.5"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>

                  {isExpanded && item.agendaDescription && (
                    <div
                      className="agenda-collapse-body"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        className="agenda-description"
                        dangerouslySetInnerHTML={{ __html: item.agendaDescription }}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </section>
  );
}
